import { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search,
  MoreVertical,
  ExternalLink,
  Trash2,
  Lock,
} from "lucide-react";
import { useNotifications } from "../../contexts/NotificationContext";
import { Project } from "../../types";
import { projectService } from "../../services/project.service";

interface ProjectsTabProps {
  searchTerm: string;
  setSearchTerm: (term: string) => void;
}

function ProjectsTab({ searchTerm, setSearchTerm }: ProjectsTabProps) {
  const [showProjectMenu, setShowProjectMenu] = useState<string | null>(null);
  const { showNotification } = useNotifications();
  const navigate = useNavigate();

  // Projects
  const [projects, setProjects] = useState<Project[]>([]);
  const [displayedProjects, setDisplayedProjects] = useState<Project[]>([]);
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);
  const [hasMoreProjects, setHasMoreProjects] = useState(true);
  const [page, setPage] = useState(1);
  const projectsPerPage = 20;
  const observerTarget = useRef<HTMLDivElement>(null);

  const loadProjects = useCallback(async () => {
    try {
      const data = await projectService.getUserProjects();
      // Sort projects by owner email
      const sortedProjects = [...data].sort((a, b) => {
        const emailA = a.owner?.email?.toLowerCase() || '';
        const emailB = b.owner?.email?.toLowerCase() || '';
        return emailA.localeCompare(emailB);
      });
      setProjects(sortedProjects);
      setHasMoreProjects(sortedProjects.length > projectsPerPage);
      setDisplayedProjects(sortedProjects.slice(0, projectsPerPage));
    } catch (error) {
      showNotification('error', 'Failed to load projects');
    }
  }, [showNotification]);

  useEffect(() => {
    setIsLoadingProjects(true);
    loadProjects().finally(() => {
      setIsLoadingProjects(false);
    });
  }, [loadProjects]);

  // Update displayed projects when search term or projects change
  useEffect(() => {
    // Filter projects based on search term
    const filteredProjects = projects?.filter(project => {
      if (!searchTerm) return true;
      const searchLower = searchTerm.toLowerCase();
      return (
        project.name.toLowerCase().includes(searchLower) ||
        project.owner?.email?.toLowerCase().includes(searchLower)
      );
    }) || [];
    
    const projectsToDisplay = searchTerm ? filteredProjects : projects;
    setDisplayedProjects(projectsToDisplay.slice(0, page * projectsPerPage));
    setHasMoreProjects(projectsToDisplay.length > page * projectsPerPage);
  }, [searchTerm, projects, page, projectsPerPage]);

  // Intersection observer for infinite scrolling
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMoreProjects && !isLoadingProjects) {
          setPage((prevPage) => prevPage + 1);
        }
      },
      { threshold: 0.5 }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [hasMoreProjects, isLoadingProjects]);

  const handleProjectAction = useCallback(
    async (action: string, project: Project) => {
      setShowProjectMenu(null);
      try {
        switch (action) {
          case "view":
            window.open(`/projects/${project.id}`, '_blank');
            break;
          case "lock":
            await projectService.lockProject(project.id);
            showNotification("success", "Project locked successfully");
            loadProjects();
            break;
          case "unlock":
            await projectService.unlockProject(project.id);
            showNotification("success", "Project unlocked successfully");
            loadProjects();
            break;
          case "delete":
            if (confirm("Are you sure you want to delete this project?")) {
              await projectService.deleteProject(project.id);
              showNotification("success", "Project deleted successfully");
              loadProjects();
            }
            break;
        }
      } catch (error) {
        showNotification("error", `Failed to ${action} project`);
      }
    },
    [showNotification, loadProjects, navigate]
  );

  return (
    <div className="projects-tab">
      <div className="mb-6">
        <div className="relative">
          <input
            type="text"
            placeholder="Filter by email or project name"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-gray-200 dark:placeholder-gray-400"
          />
          <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400 dark:text-gray-500" />
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead>
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Project Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Collaborators
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Sharing
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Last Modified
              </th>
              <th className="relative px-6 py-3">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {isLoadingProjects && displayedProjects.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-6 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                  Loading projects...
                </td>
              </tr>
            ) : displayedProjects.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-6 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                  No projects found
                </td>
              </tr>
            ) : (
              <>
                {displayedProjects.reduce<React.ReactNode[]>((acc, project, index, array) => {
                  const prevProject = index > 0 ? array[index - 1] : null;
                  const currentEmail = project.owner?.email || 'Unknown';
                  const prevEmail = prevProject?.owner?.email || 'Unknown';
                  
                  // Add a group header if this is a new email group
                  if (index === 0 || currentEmail !== prevEmail) {
                    acc.push(
                      <tr key={`group-${currentEmail}`} className="bg-gray-200 dark:bg-gray-900/30">
                        <td colSpan={6} className="px-6 py-2 text-xs text-gray-700 dark:text-gray-400">
                          {currentEmail}
                        </td>
                      </tr>
                    );
                  }
                  
                  // Add the project row
                  acc.push(
                    <tr key={project.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {project.name}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            project.is_locked
                              ? "bg-yellow-500/10 text-yellow-800 dark:text-yellow-200"
                              : "bg-green-500/10 text-green-800 dark:text-green-200"
                          }`}
                        >
                          {project.is_locked ? "Locked" : "Active"}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-1">
                          {/* Safely access collaborators if it exists */}
                          {(project as any).collaborators?.length ? (
                            <span className="text-sm text-gray-500 dark:text-gray-400">
                              {(project as any).collaborators.length}
                            </span>
                          ) : (
                            <span className="text-sm text-gray-400 dark:text-gray-500">-</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          {/* Safely check for sharing properties */}
                          {(project as any).is_shared && (
                            <span title="Shared" className="text-blue-500">
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"></path>
                                <polyline points="16 6 12 2 8 6"></polyline>
                                <line x1="12" y1="2" x2="12" y2="15"></line>
                              </svg>
                            </span>
                          )}
                          {(project as any).is_embedded && (
                            <span title="Embedded" className="text-purple-500">
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                                <line x1="3" y1="9" x2="21" y2="9"></line>
                                <line x1="9" y1="21" x2="9" y2="9"></line>
                              </svg>
                            </span>
                          )}
                          {!(project as any).is_shared && !(project as any).is_embedded && (
                            <span className="text-sm text-gray-400 dark:text-gray-500">-</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {new Date(project.updated_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="relative">
                          <button
                            onClick={() => setShowProjectMenu(project.id)}
                            className="text-gray-400 hover:text-gray-500 dark:text-gray-500 dark:hover:text-gray-400"
                          >
                            <MoreVertical className="h-5 w-5" />
                          </button>
                          {/* Add overlay for click-outside */}
                          {showProjectMenu === project.id && (
                            <div
                              className="fixed inset-0 bg-black/0 z-40"
                              onClick={() => setShowProjectMenu(null)}
                            />
                          )}
                          {showProjectMenu === project.id && (
                            <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white dark:bg-gray-800 ring-1 ring-black ring-opacity-5 dark:ring-gray-700 z-50">
                              <div className="py-1" role="menu">
                                <button
                                  onClick={() => handleProjectAction("view", project)}
                                  className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                                >
                                  <ExternalLink className="h-4 w-4" />
                                  View Project
                                </button>
                                <button
                                  onClick={() =>
                                    handleProjectAction(
                                      project.is_locked
                                        ? "unlock"
                                        : "lock",
                                      project
                                    )
                                  }
                                  className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                                >
                                  <Lock className="h-4 w-4" />
                                  {project.is_locked
                                    ? "Unlock Project"
                                    : "Lock Project"}
                                </button>
                                <button
                                  onClick={() => handleProjectAction("delete", project)}
                                  className="w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                                >
                                  <Trash2 className="h-4 w-4" />
                                  Delete Project
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                  return acc;
                }, [])}
                {hasMoreProjects && (
                  <tr>
                    <td colSpan={5} className="p-2">
                      <div 
                        ref={observerTarget} 
                        className="h-10 flex items-center justify-center"
                      >
                        {isLoadingProjects ? (
                          <div className="text-sm text-gray-500 dark:text-gray-400">Loading more projects...</div>
                        ) : (
                          <div className="text-sm text-gray-400 dark:text-gray-500">Scroll to load more</div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default ProjectsTab;
